
import React, { useState, useEffect } from 'react';
import { MesoCurriculum } from '@/services/roadmap/roadmapService';
import { DEFAULT_BELTS, Belt } from './types';
import { TimelineControls } from './TimelineControls';
import { BeltCircles } from './MicroBeltCircles';
import { TimelineTrack } from './MicroTimelineTrack';
import { BeltStripes } from './MicroBeltStripes';
import { MicroJournalTickMarks } from './MicroJournalTickMarks';
import { usePermissions } from '@/contexts/unified-permissions';
import { CurriculumDialog } from '../CurriculumDialog';
import { useStripeSpacings } from './useStripeSpacings';
import { useMicroBeltScroll } from './useMicroBeltScroll';

interface MicroBeltTimelineProps {
  data: MesoCurriculum[];
}

export const MicroBeltTimeline: React.FC<MicroBeltTimelineProps> = ({ data }) => {
  const {
    scrollContainerRef, canScrollLeft, canScrollRight, containerWidth,
    checkScrollability, scrollLeft, scrollRight
  } = useMicroBeltScroll();

  const stripeSpacings = useStripeSpacings();
  const { user } = usePermissions();

  const [selectedBelt, setSelectedBelt] = useState<Belt | null>(null);
  const [selectedCurriculum, setSelectedCurriculum] = useState<MesoCurriculum | null>(null);

  const START_X = 40;
  const BASE_BELT_SPACING = 230;

  useEffect(() => {
    if (selectedBelt) {
      const curriculum = data.find(item =>
        item.belt_color.toLowerCase() === selectedBelt.dbColor.toLowerCase() &&
        (!selectedBelt.stripes || item.stripe_count === selectedBelt.stripes)
      );
      setSelectedCurriculum(curriculum || null);
    } else {
      setSelectedCurriculum(null);
    }
  }, [selectedBelt, data]);

  const handleBeltClick = (belt: Belt) => {
    if (selectedBelt && selectedBelt.name === belt.name &&
      selectedBelt.stripes === belt.stripes) {
      setSelectedBelt(null);
    } else {
      setSelectedBelt(belt);
    }
  };

  const handleCloseDialog = () => {
    setSelectedBelt(null);
  };

  const startDate = new Date();
  startDate.setFullYear(startDate.getFullYear() - 1);
  const endDate = new Date();

  // Center timeline on user's belt/stripe on mount or update
  useEffect(() => {
    if (!scrollContainerRef.current || !user) return;

    const userBeltRank = user.user_metadata?.belt_rank || "white";
    const userStripes = user.user_metadata?.stripes ?? "0";
    if (userBeltRank.toLowerCase() === "white") {
      if (userStripes === 0 || userStripes === "0" || userStripes === "" || userStripes === "no stripes") {
        return;
      }
    }

    let stripeIdx: number;
    if (typeof userStripes === "number") {
      stripeIdx = Math.max(0, Math.min(4, userStripes));
    } else {
      stripeIdx = parseInt(userStripes, 10);
      stripeIdx = isNaN(stripeIdx) ? 0 : Math.max(0, Math.min(4, stripeIdx));
    }

    const userBeltIdx = DEFAULT_BELTS.findIndex(
      (belt) => belt.dbColor.toLowerCase() === userBeltRank.toLowerCase()
    );
    if (userBeltIdx === -1) return;

    let beltStripePos = START_X;
    for (let i = 0; i < userBeltIdx; i++) {
      for (let j = 0; j < 5; j++) {
        const spacingIndex = i * 5 + j;
        beltStripePos += stripeSpacings[spacingIndex] || BASE_BELT_SPACING / 5;
      }
    }
    for (let i = 0; i < stripeIdx; i++) {
      const spacingIndex = userBeltIdx * 5 + i;
      beltStripePos += stripeSpacings[spacingIndex] || BASE_BELT_SPACING / 5;
    }

    if (userBeltRank.toLowerCase() === "white" && stripeIdx > 0) {
      beltStripePos += 30;
    }

    const spaceToCenter = containerWidth / 2;
    let scrollTo = beltStripePos - spaceToCenter;
    const container = scrollContainerRef.current;
    const maxScroll = container.scrollWidth - containerWidth;
    scrollTo = Math.max(0, Math.min(maxScroll, scrollTo));

    container.scrollTo({ left: scrollTo, behavior: 'smooth' });
  }, [containerWidth, user, stripeSpacings, scrollContainerRef]);

  return (
    <div className="relative w-full">
      <TimelineControls
        onScrollLeft={scrollLeft}
        onScrollRight={scrollRight}
        canScrollLeft={canScrollLeft}
        canScrollRight={canScrollRight}
      />

      <div
        className="overflow-x-auto pb-4 hide-scrollbar w-full mx-auto"
        ref={scrollContainerRef}
        onScroll={checkScrollability}
        style={{
          paddingLeft: '4px',
          paddingRight: '12px',
          scrollbarWidth: 'none',
          scrollPaddingLeft: '0px',
          maxWidth: '100%',
          WebkitOverflowScrolling: 'touch',
        }}
      >
        <div className="relative h-[110px]" style={{ width: '100%', minWidth: '500px' }}>
          <TimelineTrack
            belts={DEFAULT_BELTS}
            baseBeltSpacing={BASE_BELT_SPACING}
            stripeSpacings={stripeSpacings}
          />
          <BeltCircles
            belts={DEFAULT_BELTS}
            baseBeltSpacing={BASE_BELT_SPACING}
            stripeSpacings={stripeSpacings}
            onBeltClick={handleBeltClick}
          />
          <BeltStripes
            belts={DEFAULT_BELTS}
            baseBeltSpacing={BASE_BELT_SPACING}
            stripeSpacings={stripeSpacings}
            onStripeClick={handleBeltClick}
          />
          <MicroJournalTickMarks
            startDate={startDate}
            endDate={endDate}
            belts={DEFAULT_BELTS}
            baseBeltSpacing={BASE_BELT_SPACING}
            stripeSpacings={stripeSpacings}
          />
        </div>
      </div>

      <CurriculumDialog
        belt={selectedBelt}
        curriculum={selectedCurriculum}
        onClose={handleCloseDialog}
      />
    </div>
  );
};
